import { useState, useEffect, useCallback } from 'react';
import { TopBar } from '../components/layout/TopBar';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { Star, TrendingUp, TrendingDown, Minus, Smile, Meh, Frown, Loader2, Save, RefreshCw, MessageSquare } from 'lucide-react';

const SCORE_COLOR = (s) => s >= 9 ? '#22c55e' : s >= 7 ? '#f59e0b' : '#ef4444';
const SCORE_BG = (s) => s >= 9 ? 'bg-green-100 text-green-700' : s >= 7 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700';

export default function FeedbackNPS() {
  const [stats, setStats] = useState({ score: 0, total: 0, promoters: 0, passives: 0, detractors: 0, avg: 0 });
  const [responses, setResponses] = useState([]);
  const [config, setConfig] = useState({ enabled: true, delay_hours: 2, question: '¿Cómo calificarías tu experiencia?', followup_question: '¿Qué podríamos mejorar?' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('overview');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [stR, rR, cR] = await Promise.all([api.get('/nps/stats'), api.get('/nps/responses'), api.get('/nps/config')]);
      setStats(stR.data);
      setResponses(rR.data || []);
      setConfig(cR.data);
    } catch { toast.error('Error al cargar datos'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const saveConfig = async () => {
    setSaving(true);
    try { await api.patch('/nps/config', config); toast.success('Configuración guardada'); }
    catch { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  const npsScore = stats.score;

  if (loading) return (
    <div className="h-screen flex flex-col"><TopBar title="Feedback & NPS" helpSection="nps" />
      <div className="flex-1 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <TopBar title="Feedback & NPS" helpSection="nps" actions={
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchAll} className="h-8"><RefreshCw className="h-3.5 w-3.5 mr-1" /> Actualizar</Button>
          <Button onClick={saveConfig} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white h-8">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />} Guardar
          </Button>
        </div>
      } />

      <div className="flex-1 p-6 max-w-6xl mx-auto w-full space-y-6">
        {/* NPS Gauge */}
        <div className="grid grid-cols-4 gap-4">
          {/* Main NPS Score */}
          <Card className="col-span-1 border-0 shadow-sm overflow-hidden">
            <CardContent className="p-6 text-center">
              <p className="text-xs uppercase tracking-wider text-slate-400 mb-3 font-semibold">Net Promoter Score</p>
              <div className="relative w-32 h-32 mx-auto mb-3">
                <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                  <circle cx="50" cy="50" r="40" fill="none" stroke="#f1f5f9" strokeWidth="10" />
                  <circle cx="50" cy="50" r="40" fill="none"
                    stroke={npsScore >= 50 ? '#22c55e' : npsScore >= 0 ? '#f59e0b' : '#ef4444'}
                    strokeWidth="10" strokeDasharray={`${Math.abs(npsScore) * 2.51} 251`}
                    strokeLinecap="round" />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-3xl font-black" style={{ color: npsScore >= 50 ? '#22c55e' : npsScore >= 0 ? '#f59e0b' : '#ef4444' }}>
                    {npsScore}
                  </span>
                  <span className="text-[10px] text-slate-400">/ 100</span>
                </div>
              </div>
              <div className={cn("px-3 py-1 rounded-full text-xs font-semibold mx-auto w-fit",
                npsScore >= 70 ? 'bg-green-100 text-green-700' : npsScore >= 30 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700')}>
                {npsScore >= 70 ? '🌟 Excelente' : npsScore >= 30 ? '👍 Bueno' : npsScore >= 0 ? '⚠️ Mejorable' : '🔴 Crítico'}
              </div>
            </CardContent>
          </Card>

          {/* Breakdown */}
          <Card className="col-span-3 border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="grid grid-cols-3 gap-6 mb-6">
                {[
                  { l: 'Promotores', n: stats.promoters, t: stats.total, icon: Smile, c: '#22c55e', bg: 'bg-green-50', range: '9–10' },
                  { l: 'Pasivos', n: stats.passives, t: stats.total, icon: Meh, c: '#f59e0b', bg: 'bg-amber-50', range: '7–8' },
                  { l: 'Detractores', n: stats.detractors, t: stats.total, icon: Frown, c: '#ef4444', bg: 'bg-red-50', range: '0–6' },
                ].map(s => (
                  <div key={s.l} className={cn("rounded-2xl p-4", s.bg)}>
                    <div className="flex items-center gap-2 mb-2">
                      <s.icon className="h-5 w-5" style={{ color: s.c }} />
                      <span className="text-xs font-semibold text-slate-600">{s.l} ({s.range})</span>
                    </div>
                    <div className="text-3xl font-black" style={{ color: s.c }}>{s.n}</div>
                    <div className="text-xs text-slate-500 mt-1">{s.t > 0 ? Math.round(s.n / s.t * 100) : 0}% del total</div>
                    <div className="mt-3 h-1.5 bg-white/60 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${s.t > 0 ? (s.n / s.t * 100) : 0}%`, backgroundColor: s.c }} />
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-6 text-sm text-slate-500 border-t pt-4">
                <div><span className="font-semibold text-slate-800">{stats.total}</span> respuestas totales</div>
                <div><span className="font-semibold text-slate-800">{stats.avg}</span> puntuación media</div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-slate-100 rounded-xl p-1 w-fit">
          {[{ k: 'overview', l: 'Respuestas' }, { k: 'config', l: 'Configuración' }].map(t => (
            <button key={t.k} onClick={() => setTab(t.k)}
              className={cn("px-4 py-2 rounded-lg text-sm font-medium transition-all",
                tab === t.k ? "bg-white shadow-sm text-slate-800" : "text-slate-500 hover:text-slate-700")}>
              {t.l}
            </button>
          ))}
        </div>

        {tab === 'overview' && (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-0">
              {responses.length === 0 ? (
                <div className="text-center py-16 text-slate-400">
                  <MessageSquare className="h-10 w-10 mx-auto mb-3 opacity-30" />
                  <p className="font-medium">Sin respuestas aún</p>
                  <p className="text-sm mt-1">Las encuestas enviadas a tus clientes aparecerán aquí</p>
                </div>
              ) : (
                <div className="divide-y">
                  {responses.map((r, i) => (
                    <div key={i} className="p-4 hover:bg-slate-50 flex items-start gap-4">
                      <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center text-lg font-black flex-shrink-0", SCORE_BG(r.score))}>
                        {r.score}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-slate-800">{r.guest_name || 'Cliente'}</p>
                          <span className={cn("text-[10px] px-2 py-0.5 rounded-full font-medium",
                            r.score >= 9 ? 'bg-green-100 text-green-700' : r.score >= 7 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700')}>
                            {r.score >= 9 ? 'Promotor' : r.score >= 7 ? 'Pasivo' : 'Detractor'}
                          </span>
                        </div>
                        {r.comment && <p className="text-sm text-slate-600 mt-1">"{r.comment}"</p>}
                        <p className="text-[11px] text-slate-400 mt-1">{r.created_at ? new Date(r.created_at).toLocaleDateString('es-ES') : ''}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {tab === 'config' && (
          <Card className="border-0 shadow-sm max-w-lg">
            <CardHeader><CardTitle className="text-base">Configuración de encuestas</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">Encuestas automáticas</Label>
                  <p className="text-xs text-slate-400 mt-0.5">Enviar NPS automáticamente tras cada visita</p>
                </div>
                <Switch checked={!!config.enabled} onCheckedChange={v => setConfig(c => ({ ...c, enabled: v }))} />
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Demora envío (horas después de la visita)</Label>
                <Input type="number" min={0} value={config.delay_hours} onChange={e => setConfig(c => ({ ...c, delay_hours: +e.target.value }))} className="text-sm max-w-xs" />
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Pregunta principal</Label>
                <Input value={config.question} onChange={e => setConfig(c => ({ ...c, question: e.target.value }))} className="text-sm" />
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Pregunta de seguimiento (opcional)</Label>
                <Input value={config.followup_question} onChange={e => setConfig(c => ({ ...c, followup_question: e.target.value }))} className="text-sm" />
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
